import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserState, User } from '../models/user.model';

@Injectable({
    providedIn: 'root'
})
export class UserStateService {
    private readonly TOKEN_KEY = 'access_token';
    private readonly CUSTOMER_KEY = 'customer_data';
    
    // Separate subjects for token and customer
    private tokenSubject: BehaviorSubject<string | null>;
    private customerSubject: BehaviorSubject<User | null>;

    // Public observables
    public token$: Observable<string | null>;
    public customer$: Observable<User | null>;

    constructor() {
        // Initialize with persisted data if available
        const initialToken = localStorage.getItem(this.TOKEN_KEY);
        const initialCustomer = localStorage.getItem(this.CUSTOMER_KEY);

        this.tokenSubject = new BehaviorSubject<string | null>(initialToken);
        this.customerSubject = new BehaviorSubject<User | null>(
            initialCustomer ? JSON.parse(initialCustomer) : null
        );

        this.token$ = this.tokenSubject.asObservable();
        this.customer$ = this.customerSubject.asObservable();

        // Optional: Persist state changes to localStorage
        this.token$.subscribe(token => {
            if (token) {
                localStorage.setItem(this.TOKEN_KEY, token);
            } else {
                localStorage.removeItem(this.TOKEN_KEY);
            }
        });

        this.customer$.subscribe(customer => {
            if (customer) {
                localStorage.setItem(this.CUSTOMER_KEY, JSON.stringify(customer));
            } else {
                localStorage.removeItem(this.CUSTOMER_KEY);
            }
        });
    }

    setUser(userData: UserState) {
        this.tokenSubject.next(userData.access_token);
        this.customerSubject.next(userData.customer);
    }

    getUser(): UserState | null {
        const token = this.tokenSubject.value;
        const customer = this.customerSubject.value;

        if (!token || !customer) return null;

        return {
            access_token: token,
            customer: customer
        };
    }

    clearUser() {
        this.tokenSubject.next(null);
        this.customerSubject.next(null);
    }

    isLoggedIn(): boolean {
        return !!this.tokenSubject.value;
    }

    getAccessToken(): string | null {
        return this.tokenSubject.value;
    }

    getCustomer(): User | null {
        return this.customerSubject.value;
    }

    updateCustomerData(customerData: Partial<User>) {
        const currentCustomer = this.customerSubject.value;
        if (currentCustomer) {
            this.customerSubject.next({
                ...currentCustomer,
                ...customerData
            });
        }
    }

    // Optional: Method to disable persistence
    disablePersistence() {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.CUSTOMER_KEY);
    }

    // Optional: Method to enable persistence
    enablePersistence() {
        const token = this.tokenSubject.value;
        const customer = this.customerSubject.value;

        if (token) {
            localStorage.setItem(this.TOKEN_KEY, token);
        }
        if (customer) {
            localStorage.setItem(this.CUSTOMER_KEY, JSON.stringify(customer));
        }
    }
} 